// src/hooks/useCommsFieldVersions.js
// Field-locked hook for comms.field_versions.
//
// One row per version of a comms free-text field. Versions are created
// deliberately — the + button beside a field's version chips — never by the
// field's own autosave. Rows are grouped per field and labelled v.01, v.02…
// client-side from creation order, the same scheme the Timelines page uses
// (see useTimelineVersions).
import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';

const comms = supabase.schema('comms');

const requireSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw new Error('Authentication error: ' + error.message);
  if (!session?.user) throw new Error('Please sign in to perform this action.');
  return session.user.id;
};

function mapRow(row, index) {
  const autoLabel = 'v.' + String(index + 1).padStart(2, '0');
  return {
    id: String(row.id),
    // `label` is what the chip shows: the user's name for the version when
    // they renamed it, otherwise its position in the field's history.
    label: (row.label || '').trim() || autoLabel,
    autoLabel,
    isRenamed: !!(row.label || '').trim(),
    field: row.field,
    content: row.content || '',
    editedById: row.edited_by_id == null ? null : Number(row.edited_by_id),
    editedByName: row.edited_by_name || '',
    createdAt: row.created_at,
  };
}

export function useCommsFieldVersions(entity, entityId) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch every version of every field on this entity in one query, oldest first
  const fetchVersions = useCallback(async () => {
    if (!entity || !entityId) { setRows([]); return; }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await comms
        .from('field_versions')
        .select('id, field, label, content, edited_by_id, edited_by_name, created_at')
        .eq('entity', entity)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });
      if (err) throw err;
      setRows(data || []);
    } catch (e) {
      setError(e.message || String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [entity, entityId]);

  useEffect(() => { fetchVersions(); }, [fetchVersions]);

  // { [field]: [version, …] } — labels are positional within each field
  const versionsByField = useMemo(() => {
    const out = {};
    rows.forEach(row => {
      const list = out[row.field] || (out[row.field] = []);
      list.push(mapRow(row, list.length));
    });
    return out;
  }, [rows]);

  // Capture the field's current wording as a new version. Called from the +
  // button — one click, one version, nothing merged or rewritten.
  const saveVersion = useCallback(async ({ field, content, editedById = null, editedByName = '' }) => {
    await requireSession();
    const { error: err } = await comms.from('field_versions').insert({
      entity,
      entity_id: entityId,
      field,
      content: content || '',
      edited_by_id: editedById == null ? null : Number(editedById),
      edited_by_name: editedByName || null,
    });
    if (err) throw err;
    await fetchVersions();
  }, [entity, entityId, fetchVersions]);

  // Name a version ("draft", "for publishing"…). An empty name clears the
  // override so the chip falls back to its positional v.01 label.
  const renameVersion = useCallback(async (versionId, label) => {
    await requireSession();
    const { error: err } = await comms
      .from('field_versions')
      .update({ label: (label || '').trim() || null, updated_at: new Date().toISOString() })
      .eq('id', Number(versionId));
    if (err) throw err;
    await fetchVersions();
  }, [fetchVersions]);

  // Remove a version. The remaining ones keep their order, so the positional
  // v.01/v.02 labels are recomputed on the next fetch.
  const deleteVersion = useCallback(async (versionId) => {
    await requireSession();
    const { error: err } = await comms.from('field_versions').delete().eq('id', Number(versionId));
    if (err) throw err;
    await fetchVersions();
  }, [fetchVersions]);

  return { versionsByField, loading, error, refresh: fetchVersions, saveVersion, renameVersion, deleteVersion };
}
